/** @prettier */
import {TFunction} from 'i18next';

/**
 * 棒・折れ線混合グラフの共通設定
 * @param {ApexXAxis['categories']} xLabel X軸のラベル
 * @param {string} csvFileNameDate CSVファイルの名前（日付部分)
 * @param {string} csvFileNameTarget CSVファイルの名前（サイトの名前 + グラフの対象の名前）
 * @param {number} copYaxisMax //COPグラフのy軸の最大値
 * @param {TFunction} trans 翻訳関数
 * @returns グラフの設定データ
 */
export const barLineMixedGraphBasicSetting = (props: {
  xLabel: ApexXAxis['categories'];
  csvFileNameDate: string;
  csvFileNameTarget: string;
  copYaxisMax: number;
  trans: TFunction;
}): ApexCharts.ApexOptions => {
  const csvFileName = `E_${props.csvFileNameDate}_${props.csvFileNameTarget}`;
  return {
    chart: {
      type: 'line',
      locales: [
        {
          name: 'en',
          options: {
            toolbar: {
              selectionZoom: `${props.trans(
                'barLineMixedGraphBasicSetting.graphToolbar.selectionZoom'
              )}`,
              zoomIn: `${props.trans(
                'barLineMixedGraphBasicSetting.graphToolbar.zoomIn'
              )}`,
              zoomOut: `${props.trans(
                'barLineMixedGraphBasicSetting.graphToolbar.zoomOut'
              )}`,
              reset: `${props.trans(
                'barLineMixedGraphBasicSetting.graphToolbar.reset'
              )}`,
              exportToSVG: `${props.trans(
                'barLineMixedGraphBasicSetting.graphToolbar.exportToSVG'
              )}`,
              exportToPNG: `${props.trans(
                'barLineMixedGraphBasicSetting.graphToolbar.exportToPNG'
              )}`,
              exportToCSV: `${props.trans(
                'barLineMixedGraphBasicSetting.graphToolbar.exportToCSV'
              )}`,
            },
          },
        },
      ],
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: false,
          reset: true,
          customIcons: [],
        },
        export: {
          csv: {
            filename: csvFileName,
            columnDelimiter: ',',
            headerCategory: 'Time',
            headerValue: 'value',
            dateFormatter: timestamp => {
              if (timestamp !== undefined) {
                return new Date(timestamp).toDateString();
              }
            },
          },
          svg: {
            filename: csvFileName,
          },
          png: {
            filename: csvFileName,
          },
        },
      },
      zoom: {
        enabled: true,
      },
    },
    stroke: {
      width: [0, 4, 1, 4],
      curve: 'smooth',
      show: true,
    },
    dataLabels: {
      enabled: false,
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          legend: {
            position: 'bottom',
            offsetX: -10,
            offsetY: 0,
          },
        },
      },
    ],
    plotOptions: {
      bar: {
        dataLabels: {
          position: 'center',
        },
        borderRadius: 5,
        borderRadiusApplication: 'end',
      },
    },
    xaxis: {
      title: {
        text: `${props.trans('barLineMixedGraphBasicSetting.xAxisTitle')}`,
        style: {fontFamily: 'DINNextLTPro-Bold'},
      },
      categories: props.xLabel,
      tickPlacement: 'on',
      tickAmount: 56,
      labels: {
        style: {fontFamily: 'DINNextLTPro-Regular'},
        hideOverlappingLabels: true,
      },
      tooltip: {style: {fontFamily: 'DINNextLTPro-Regular'}},
    },
    yaxis: [
      {
        seriesName: 'Energy amount',
        title: {
          text: `${props.trans('barLineMixedGraphBasicSetting.yAxisTitle')}`,
          style: {fontFamily: 'DINNextLTPro-Bold'},
        },
        labels: {style: {fontFamily: 'DINNextLTPro-Regular'}},
      },
      {
        seriesName: 'COP',
        opposite: true,
        title: {
          text: `${props.trans(
            'barLineMixedGraphBasicSetting.yAxisOppositeTitle'
          )}`,
          style: {fontFamily: 'DINNextLTPro-Bold'},
        },
        labels: {style: {fontFamily: 'DINNextLTPro-Regular'}},
        max: props.copYaxisMax,
      },
      {
        seriesName: 'Energy amount',
        show: false,
        title: {style: {fontFamily: 'DINNextLTPro-Bold'}},
        labels: {style: {fontFamily: 'DINNextLTPro-Regular'}},
      },
      {
        seriesName: 'COP',
        opposite: true,
        show: false,
        title: {style: {fontFamily: 'DINNextLTPro-Bold'}},
        labels: {style: {fontFamily: 'DINNextLTPro-Regular'}},
        max: props.copYaxisMax,
      },
    ],
    legend: {
      position: 'right',
      fontFamily: 'DINNextLTPro-Regular',
      offsetY: 40,
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      style: {fontFamily: 'DINNextLTPro-Regular'},
      y: {
        formatter: function (value, {seriesIndex}) {
          //1つ目のデータと3つ目のデータ（熱量）はkWhを付ける
          if (seriesIndex % 2 === 0) {
            return value + 'kWh';
            //COPは単位なし
          } else {
            return String(value);
          }
        },
      },
    },
  };
};
